import React, { useState, useEffect } from "react";
import {
  AlertTriangle, Skull, CheckCircle, X, Loader2, ChevronDown,
  CheckCircle as CheckCircleIcon, XCircle as XCircleIcon,
} from "lucide-react";
import axios from "axios";
import { useSuperAdmin } from "../../context/SuperAdminContext";
import DoneByBadge from "./DoneByBatch";

const BASE_URL = process.env.REACT_APP_BASE_URL;

const InlineBanner = ({ msg, type }) => {
  if (!msg) return null;
  return (
    <div className={`px-3 py-1.5 rounded-md text-xs font-medium flex items-center gap-1.5 mt-2 ${
      type === "success" ? "bg-green-50 text-green-700 border border-green-200" : "bg-red-50 text-red-700 border border-red-200"
    }`}>
      {type === "success" ? <CheckCircleIcon className="h-3.5 w-3.5" /> : <XCircleIcon className="h-3.5 w-3.5" />}
      {msg}
    </div>
  );
};

const BlacklistStatus = ({
  workerId,
  workerName = "",
  isUTVMember = false,
  initialIsBlacklisted = false,
  initialEligibleCompanyName = "",
  initialEligibleLocation = "",
  initialReason = "",
  onUpdate,
  // done-by metadata from parent (verificationMeta.blacklist)
  markedByName = null,
  markedAt = null,
}) => {
  const { user } = useSuperAdmin();
  const [isBlacklisted, setIsBlacklisted] = useState(initialIsBlacklisted);
  const [eligibleCompanyName, setEligibleCompanyName] = useState(initialEligibleCompanyName);
  const [eligibleLocation, setEligibleLocation] = useState(initialEligibleLocation);
  const [blacklistReason, setBlacklistReason] = useState(initialReason);
  const [message, setMessage] = useState({ text: "", type: "success" });
  const [localMeta, setLocalMeta] = useState({ byName: markedByName, at: markedAt });

  const [showRemoveBlacklistModal, setShowRemoveBlacklistModal] = useState(false);
  const [isRemovingBlacklist, setIsRemovingBlacklist] = useState(false);
  const [showBlacklistModal, setShowBlacklistModal] = useState(false);
  const [isSubmittingBlacklist, setIsSubmittingBlacklist] = useState(false);
  const [blacklistReasons, setBlacklistReasons] = useState([]);
  const [selectedReason, setSelectedReason] = useState(null);
  const [isReasonOpen, setIsReasonOpen] = useState(false);
  const [loadingReasons, setLoadingReasons] = useState(false);
  const [showRestrictedModal, setShowRestrictedModal] = useState(false);
  const [isSavingRestricted, setIsSavingRestricted] = useState(false);
  const [locations, setLocations] = useState([]);
  const [selectedLocation, setSelectedLocation] = useState("");
  const [selectedlocationobject, setselectedlocationobject] = useState([]);
  const [isLocationOpen, setIsLocationOpen] = useState(false);
  const [loadingLocations, setLoadingLocations] = useState(false);
  const [companiesList, setCompaniesList] = useState([]);
  const [selectedCompanyId, setSelectedCompanyId] = useState("");
  const [isCompanyOpen, setIsCompanyOpen] = useState(false);
  const [loadingCompaniesList, setLoadingCompaniesList] = useState(false);

  useEffect(() => {
    setIsBlacklisted(initialIsBlacklisted);
    setEligibleCompanyName(initialEligibleCompanyName);
    setEligibleLocation(initialEligibleLocation);
    setBlacklistReason(initialReason);
  }, [initialIsBlacklisted, initialEligibleCompanyName, initialEligibleLocation, initialReason]);

  useEffect(() => { setLocalMeta({ byName: markedByName, at: markedAt }); }, [markedByName, markedAt]);

  useEffect(() => {
    if (showBlacklistModal) { fetchBlacklistReasons(); setSelectedReason(null); }
  }, [showBlacklistModal]);

  useEffect(() => {
    if (showRestrictedModal && locations.length === 0) {
      setLoadingLocations(true);
      fetchLocations().finally(() => setLoadingLocations(false));
    }
  }, [showRestrictedModal]);

  useEffect(() => {
    if (!selectedLocation) { setCompaniesList([]); setSelectedCompanyId(""); return; }
    const fetch = async () => {
      try {
        setLoadingCompaniesList(true);
        const token = localStorage.getItem("superadmintoken");
        const res = await axios.get(`${BASE_URL}/onDemandCompanies/list-companies/${selectedlocationobject._id}`, { headers: { Authorization: `Bearer ${token}` } });
        setCompaniesList(res.data?.companies || []);
      } catch { setCompaniesList([]); }
      finally { setLoadingCompaniesList(false); }
    };
    fetch();
  }, [selectedLocation]);

  const flash = (msg, type = "success") => {
    setMessage({ text: msg, type });
    setTimeout(() => setMessage({ text: "", type: "success" }), 3500);
  };

  const fetchBlacklistReasons = async () => {
    try {
      setLoadingReasons(true);
      const mockResponse = { data: { success: true, reasons: [
        { _id: "1", label: "Abusive Behaviour" }, { _id: "2", label: "Fraud Activity" },
        { _id: "3", label: "Fake Documents" }, { _id: "4", label: "Policy Violation" },
        { _id: "5", label: "Caught Stealing" }, { _id: "6", label: "Criminal Background" },
        // { _id: "7", label: "Underage" },{ _id: "6", label: "Overage" },
      ]}};
      if (mockResponse.data.success) setBlacklistReasons(mockResponse.data.reasons);
    } catch { setBlacklistReasons([]); }
    finally { setLoadingReasons(false); }
  };

  const fetchLocations = async () => {
    try {
      const token = localStorage.getItem("superadmintoken");
      const res = await axios.get(`${BASE_URL}/lenskart/locations`, { headers: { Authorization: `Bearer ${token}` } });
      if (res.data.success && Array.isArray(res.data.locations)) setLocations(res.data.locations);
    } catch { /* silent */ }
  };

  const handleRemoveBlacklist = async () => {
    setIsRemovingBlacklist(true);
    try {
      const token = localStorage.getItem("superadmintoken");
      const res = await fetch(`${BASE_URL}/lenskart-admin/blacklist-toggle/${workerId}`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ isBlacklisted: false }),
      });
      const data = await res.json();
      if (data.success === true) {
        setIsBlacklisted(false);
        setBlacklistReason("");
        setLocalMeta({ byName: data.markedByName || localMeta.byName, at: new Date().toISOString() });
        setShowRemoveBlacklistModal(false);
        flash("Worker removed from blacklist successfully.", "success");
        if (onUpdate) onUpdate(false, "", "");
      } else {
        flash(data.message || "Failed to remove from blacklist.", "error");
      }
    } catch (err) {
      flash(err.message || "Failed to remove from blacklist.", "error");
    } finally { setIsRemovingBlacklist(false); }
  };

  const handleBlacklistSubmit = async () => {
    if (!selectedReason) return;
    setIsSubmittingBlacklist(true);
    try {
      const token = localStorage.getItem("superadmintoken");
      const res = await fetch(`${BASE_URL}/lenskart-admin/blacklist-toggle/${workerId}`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ isBlacklisted: true, reason: selectedReason }),
      });
      const data = await res.json();
      if (data.success === true) {
        setIsBlacklisted(true);
        setBlacklistReason(selectedReason);
        setLocalMeta({ byName: data.markedByName || localMeta.byName, at: new Date().toISOString() });
        setShowBlacklistModal(false);
        setSelectedReason(null);
        flash("Worker has been blacklisted.", "success");
        if (onUpdate) onUpdate(true, eligibleCompanyName, eligibleLocation);
      }
    } catch (err) {
      setShowBlacklistModal(false);
      flash(err.message || "Failed to blacklist worker.", "error");
    } finally { setIsSubmittingBlacklist(false); }
  };

  const handleSaveRestricted = async () => {
    if (!selectedLocation || !selectedCompanyId) return;
    setIsSavingRestricted(true);
    try {
      const companyObj = companiesList.find((c) => c._id === selectedCompanyId);
      setEligibleCompanyName(companyObj?.name || "");
      setEligibleLocation(selectedLocation);
      setShowRestrictedModal(false);
      flash("Worker eligibility updated successfully.", "success");
      if (onUpdate) onUpdate(isBlacklisted, companyObj?.name || "", selectedLocation);
    } catch (err) {
      setShowRestrictedModal(false);
      flash(err.message || "Failed to update eligibility.", "error");
    } finally { setIsSavingRestricted(false); }
  };

  return (
    <>
      <div className="p-3 rounded-lg border border-gray-200 bg-gray-50">
        <div className="flex items-center gap-1.5 mb-2 text-red-600">
          <AlertTriangle className="h-4 w-4" />
          <span className="text-xs font-semibold">Blacklist Status</span>
        </div>

        {isBlacklisted ? (
          <div className="p-2 rounded-md border border-red-200 bg-red-50">
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <div className="flex items-center gap-1.5">
                  <Skull className="h-3.5 w-3.5 text-red-600" />
                  <span className="text-xs font-semibold text-red-700">Worker is Blacklisted</span>
                </div>
                {blacklistReason && (
                  <div className="mt-1 pl-5 text-xs">
                    <span className="font-medium text-red-700">Reason: </span>
                    <span className="text-red-600">{blacklistReason}</span>
                  </div>
                )}
                {/* ── done-by INSIDE the red blacklisted box ── */}
                <div className="pl-5">
                  <DoneByBadge doneByName={localMeta.byName} doneAt={localMeta.at} label="Marked" />
                </div>
              </div>
              <button
                type="button"
                onClick={() => setShowRemoveBlacklistModal(true)}
                className="ml-2 flex-shrink-0 px-2 py-1 text-xs bg-red-600 text-white rounded-md hover:bg-red-700"
              >
                Remove
              </button>
            </div>
          </div>
        ) : (
          <div className="flex items-center justify-between p-2 rounded-md border border-gray-200 bg-white">
            <div className="flex items-center gap-1.5">
              <CheckCircle className="h-3.5 w-3.5 text-green-500" />
              <span className="text-xs font-medium text-gray-700">Not Blacklisted</span>
              {eligibleCompanyName && (
                <span className="ml-1 px-1.5 py-0.5 text-[10px] font-medium bg-green-100 text-green-800 border border-green-300 rounded">
                  {eligibleCompanyName}
                </span>
              )}
            </div>
            <div className="flex items-center gap-1">
              <button
                type="button"
                onClick={() => setShowBlacklistModal(true)}
                className="flex items-center gap-1 px-2 py-1 text-xs bg-gray-800 text-white rounded-md hover:bg-black"
              >
                <Skull size={10} /> Blacklist
              </button>
            </div>
          </div>
        )}

        <InlineBanner msg={message.text} type={message.type} />
      </div>

      {/* Remove from Blacklist Modal */}
      {showRemoveBlacklistModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[1200] px-3">
          <div className="bg-white w-full max-w-sm rounded-lg p-4 shadow-2xl">
            <div className="flex flex-col items-center text-center gap-2 mb-4">
              <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center">
                <Skull size={24} className="text-red-600" />
              </div>
              <h3 className="text-base font-semibold">Remove from Blacklist?</h3>
              <p className="text-xs text-gray-500">
                Remove <span className="font-semibold">{workerName || "this worker"}</span> from blacklist?
              </p>
              {blacklistReason && (
                <p className="text-xs bg-red-50 p-2 rounded-md w-full">
                  <span className="font-medium">Reason: </span>
                  <span className="text-red-600">{blacklistReason}</span>
                </p>
              )}
            </div>
            <div className="flex gap-2">
              <button onClick={() => setShowRemoveBlacklistModal(false)} disabled={isRemovingBlacklist} className="flex-1 px-3 py-1.5 border border-gray-300 rounded-md text-xs text-gray-700 hover:bg-gray-50">Cancel</button>
              <button onClick={handleRemoveBlacklist} disabled={isRemovingBlacklist} className="flex-1 px-3 py-1.5 bg-red-600 text-white rounded-md text-xs hover:bg-red-700 flex items-center justify-center gap-1">
                {isRemovingBlacklist ? <Loader2 className="h-3 w-3 animate-spin" /> : "Yes, Remove"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Blacklist Modal */}
      {showBlacklistModal && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-[1200] px-3">
          <div className="bg-white w-full max-w-md rounded-lg p-4 shadow-2xl">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-1.5">
                <Skull size={16} className="text-gray-800" />
                <h2 className="text-sm font-semibold">Blacklist Worker</h2>
              </div>
              <button onClick={() => { setShowBlacklistModal(false); setSelectedReason(null); }} className="text-gray-400 hover:text-gray-600"><X size={16} /></button>
            </div>
            <div className="relative mb-3">
              <label className="text-xs font-medium text-gray-600 mb-1 block">Reason <span className="text-red-500">*</span></label>
              <button
                type="button"
                disabled={loadingReasons}
                onClick={() => setIsReasonOpen((v) => !v)}
                className={`w-full px-3 py-1.5 text-xs border rounded-md flex justify-between items-center bg-white ${loadingReasons ? "bg-gray-100 text-gray-400 cursor-not-allowed" : "hover:border-red-400"}`}
              >
                <span className={selectedReason ? "text-gray-900" : "text-gray-400"}>
                  {loadingReasons ? "Loading reasons..." : selectedReason || "Select blacklist reason"}
                </span>
                <ChevronDown className={`h-3.5 w-3.5 transition-transform ${isReasonOpen ? "rotate-180" : ""}`} />
              </button>
              {isReasonOpen && !loadingReasons && (
                <div className="absolute left-0 right-0 z-50 mt-1 bg-white border rounded-lg shadow-lg max-h-52 overflow-y-auto">
                  {blacklistReasons.length === 0
                    ? <div className="px-4 py-3 text-sm text-gray-500 text-center">No reasons available</div>
                    : blacklistReasons.map((reason) => (
                      <button key={reason._id} type="button" onClick={() => { setSelectedReason(reason.label); setIsReasonOpen(false); }}
                        className={`w-full px-4 py-2 text-left text-sm transition-colors ${selectedReason === reason.label ? "bg-red-50 text-red-700 font-semibold" : "hover:bg-gray-100"}`}
                      >{reason.label}</button>
                    ))}
                </div>
              )}
            </div>
            <div className="flex gap-2">
              <button onClick={() => { setShowBlacklistModal(false); setSelectedReason(null); }} disabled={isSubmittingBlacklist} className="flex-1 px-3 py-1.5 border border-gray-300 rounded-md text-xs text-gray-700 hover:bg-gray-50">Cancel</button>
              <button disabled={!selectedReason || isSubmittingBlacklist} onClick={handleBlacklistSubmit} className="flex-1 px-3 py-1.5 bg-red-600 text-white rounded-md text-xs hover:bg-red-700 flex items-center justify-center gap-1">
                {isSubmittingBlacklist ? <Loader2 className="h-3 w-3 animate-spin" /> : "Confirm"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Eligibility Modal */}
      {showRestrictedModal && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-[1200] px-3">
          <div className="bg-white w-full max-w-md rounded-lg p-4 shadow-2xl">
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-sm font-semibold">Edit Eligibility</h2>
              <button onClick={() => { setShowRestrictedModal(false); setSelectedLocation(""); setSelectedCompanyId(""); }} className="text-gray-400 hover:text-gray-600"><X size={16} /></button>
            </div>
            <div className="space-y-3 mb-3">
              <div>
                <label className="text-xs text-gray-600 mb-0.5 block">Location *</label>
                {isUTVMember ? (
                  <div className="relative">
                    <button type="button" onClick={() => setIsLocationOpen((v) => !v)} className="w-full px-2 py-1.5 text-xs border rounded-md text-left flex justify-between items-center bg-white">
                      {loadingLocations ? "Loading..." : selectedLocation || "Select Location"}
                      <ChevronDown className={`h-3 w-3 ${isLocationOpen ? "rotate-180" : ""}`} />
                    </button>
                    {isLocationOpen && !loadingLocations && (
                      <div className="absolute z-50 mt-1 w-full bg-white shadow-lg rounded-md py-0.5 max-h-36 overflow-auto border">
                        {locations.map((loc) => (
                          <button key={loc._id || loc} type="button" onClick={() => { setSelectedLocation(loc.name); setselectedlocationobject(loc); setIsLocationOpen(false); setSelectedCompanyId(""); }} className="block w-full text-left px-2 py-1 text-xs hover:bg-indigo-50">{loc.name}</button>
                        ))}
                      </div>
                    )}
                  </div>
                ) : (
                  <input type="text" readOnly value={selectedLocation || eligibleLocation} className="w-full px-2 py-1.5 text-xs border rounded-md bg-gray-100" />
                )}
              </div>
              <div>
                <label className="text-xs text-gray-600 mb-0.5 block">Company *</label>
                {isUTVMember ? (
                  <div className="relative">
                    <button type="button" disabled={!selectedLocation || loadingCompaniesList} onClick={() => selectedLocation && setIsCompanyOpen((v) => !v)}
                      className={`w-full px-2 py-1.5 text-xs border rounded-md text-left flex justify-between items-center ${!selectedLocation || loadingCompaniesList ? "bg-gray-100 text-gray-400 cursor-not-allowed" : "bg-white"}`}>
                      {loadingCompaniesList ? "Loading..." : companiesList.find((c) => c._id === selectedCompanyId)?.name || "Select Company"}
                      <ChevronDown className="h-3 w-3" />
                    </button>
                    {isCompanyOpen && !loadingCompaniesList && (
                      <div className="absolute z-50 mt-1 w-full bg-white shadow-lg rounded-md py-0.5 max-h-36 overflow-auto border">
                        {companiesList.map((company) => (
                          <button key={company._id} type="button" onClick={() => { setSelectedCompanyId(company._id); setIsCompanyOpen(false); }} className="block w-full text-left px-2 py-1 text-xs hover:bg-indigo-50">{company.name}</button>
                        ))}
                      </div>
                    )}
                  </div>
                ) : (
                  <input type="text" readOnly value={eligibleCompanyName} className="w-full px-2 py-1.5 text-xs border rounded-md bg-gray-100" />
                )}
              </div>
            </div>
            <div className="flex gap-2">
              <button onClick={() => { setShowRestrictedModal(false); setSelectedLocation(""); setSelectedCompanyId(""); }} disabled={isSavingRestricted} className="flex-1 px-3 py-1.5 border border-gray-300 rounded-md text-xs text-gray-700 hover:bg-gray-50">Cancel</button>
              <button disabled={!selectedLocation || !selectedCompanyId || isSavingRestricted} onClick={handleSaveRestricted} className="flex-1 px-3 py-1.5 bg-indigo-600 text-white rounded-md text-xs hover:bg-indigo-700 flex items-center justify-center gap-1">
                {isSavingRestricted ? <Loader2 className="h-3 w-3 animate-spin" /> : "Save"}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default BlacklistStatus;